import React, { useState, useEffect } from "react";
import { Search, FileText, Edit, Upload } from "lucide-react";
import { getRiskColor, getStatusText, getModeIcon, getStatusColor } from "../../utils";
import EditSessionModal from "../NotesAndRecommendations/EditSessionModal";
import SharedDocumentViewer from "../SharedDocuments/SharedDocumentViewer";
import { getSharedDocumentsHistory } from "../../redux/api/Appoinment/SharedDocumentsApi";

const REMOVED_LABELS = { DELETED: "deleted by client", UNSHARED: "removed by client" };

// Helper function to format symptom levels
const formatSymptomLevel = (level) => {
  if (!level || level === "None") return "None";
  return level.charAt(0).toUpperCase() + level.toLowerCase().slice(1);
};

export default function SessionHistory({ sessions = [], onExport, onSessionUpdated }) {
  const [sessionData, setSessionData] = useState([]);
  const [filteredSessionData, setFilteredSessionData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sharedDocs, setSharedDocs] = useState({});
  const [viewing, setViewing] = useState(null);

  // Shared-document history for all sessions in one request.
  useEffect(() => {
    const ids = [...new Set(sessions.map((s) => s.appointmentId).filter(Boolean))].slice(0, 50);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    getSharedDocumentsHistory(ids)
      .then((history) => !cancelled && setSharedDocs(history))
      .catch(() => !cancelled && setSharedDocs({}));
    return () => {
      cancelled = true;
    };
  }, [sessions]);

  // Process API data and add expansion state
  useEffect(() => {
    const processedSessions = sessions
      .map((session, index) => {
        // Process prescriptions by type
        const prescriptionsByType = (session.prescriptions || []).reduce((acc, prescription) => {
          acc[prescription.prescriptionType] = prescription;
          return acc;
        }, {});

        return {
          ...session,
          expanded: index === 0, // First session expanded by default
          uniqueId: `${session.appointmentId}-${session.date}-${index}`, // Unique identifier
          // Use real API data for session findings
          sessionFindings: {
            selfHarmSymptoms: formatSymptomLevel(session.selfHarmSymptoms),
            depressionSymptoms: formatSymptomLevel(session.depressionSymptoms), 
            anxietySymptoms: formatSymptomLevel(session.anxietySymptoms),
            angerIssues: formatSymptomLevel(session.angerIssues)
          },
          // Use real API data for recommendations
          recommendations: {
            assessments: prescriptionsByType.ASSESSMENT ? {
              name: prescriptionsByType.ASSESSMENT.fileName || "Assessment",
              url: prescriptionsByType.ASSESSMENT.url
            } : null,
            exercises: prescriptionsByType.EXERCISE ? {
              name: prescriptionsByType.EXERCISE.fileName || "Exercise",
              url: prescriptionsByType.EXERCISE.url
            } : null,
            clinicalConsultation: session.clinicalConsultation ? "Yes" : "No",
            medicalConsultation: session.medicalConsultation ? "Yes" : "No"
          }
        };
      })
      // Sort by date (most recent first) and then by start time
      .sort((a, b) => {
        // First compare by date
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime(); // Most recent first
        }
        
        // If dates are equal, compare by start time
        if (a.startTime && b.startTime) {
          const timeA = new Date(`1970-01-01T${a.startTime}`);
          const timeB = new Date(`1970-01-01T${b.startTime}`);
          return timeB.getTime() - timeA.getTime(); // Most recent time first
        }
        
        return 0;
      })
      // Re-assign expanded state after sorting (first session should be expanded)
      .map((session, index) => ({
        ...session,
        expanded: index === 0
      }));
    
    setSessionData(processedSessions);
  }, [sessions]);

  // Filter sessions based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If no search query, show all sessions
      setFilteredSessionData(sessionData);
    } else {
      // Filter sessions by appointment ID or practitioner name (case-insensitive)
      const query = searchQuery.toLowerCase().trim();
      const filtered = sessionData.filter((session) => {
        const appointmentIdMatch = session.appointmentId && 
          session.appointmentId.toLowerCase().includes(query);
        
        const practitionerNameMatch = (session.counsellorFirstName || session.counsellorLastName) &&
          `${session.counsellorFirstName || ''} ${session.counsellorLastName || ''}`.toLowerCase().includes(query);
        
        return appointmentIdMatch || practitionerNameMatch;
      });
      setFilteredSessionData(filtered);
    }
  }, [sessionData, searchQuery]);

  const toggleSession = (uniqueId) => {
    // Update the main sessionData array
    setSessionData(prev => 
      prev.map(session => 
        session.uniqueId === uniqueId
          ? { ...session, expanded: !session.expanded }
          : session
      )
    );
  };

  const handleEditSession = (session) => {
    setSelectedSession(session);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedSession(null);
  };


  const getRiskBadgeStyle = (riskStatus) => {
    let backgroundColor;
    let textColor;

    switch(riskStatus) {
      case "HIGH_RISK":
        backgroundColor = "#DC2626";
        textColor = "#FFFFFF";
        break;
      case "MODERATE_RISK":
        backgroundColor = "#FF7439";
        textColor = "#FFFFFF";
        break;
      case "LOW_RISK":
        backgroundColor = "#FFEE33";
        textColor = "#171717";
        break;
      case "NO_RISK":
        backgroundColor = "#B7E049";
        textColor = "#171717";
        break;
      case null:
      case undefined:
      case "N/A":
        backgroundColor = "#B2BEB5"; // Gray for null/N/A
        textColor = "#374151"; // Dark grey text
        break;
      default:
        backgroundColor = "#B2BEB5"; // Gray for unknown values
        textColor = "#374151"; // Dark grey text
    }

    return { backgroundColor, color: textColor };
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    
    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(hours, minutes);

    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatApiDate = (session) => {
    // Format date to dd-mm-yyyy
    let dateStr = session.date || "";
    if (dateStr) {
      // Convert from yyyy-mm-dd to dd-mm-yyyy format
      const dateParts = dateStr.split('-');
      if (dateParts.length === 3) {
        dateStr = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
      }
    }
    
    if (session.startTime) {
      const formattedTime = formatTime(session.startTime);
      if (formattedTime) {
        // If date already has time, replace it. If not, append it.
        if (dateStr.includes(':') || dateStr.toLowerCase().includes('am') || dateStr.toLowerCase().includes('pm')) {
          // Date already has time, use as is for now
          return dateStr;
        } else {
          // Append formatted time to date
          return `${dateStr} ${formattedTime}`;
        }
      }
    }
    return dateStr;
  };

  return (
    <div className="w-full">
      {/* Session History Section */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-sm md:text-[16px] font-semibold text-[#171717] font-poppins">
              Session History
            </h2>
            {/* Export Button */}
            {onExport && (
              <button 
                className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-lg p-1.5 hover:bg-gray-50 transition-colors"
                onClick={onExport}
                title="Export Session History"
              >
                <Upload className="h-4 w-4 text-[#4B61FF]" />
              </button>
            )}
          </div>
          <div className="bg-[#F8FAFC] box-border flex items-center gap-2 pl-4 pr-4 sm:pr-16 py-2 relative rounded-[8px] w-full sm:w-[364px] h-[40px] border border-[#E2E8F0]">
            <Search className="size-5 text-[#64748B] shrink-0" />
            <input
              type="text"
              placeholder="Search by Apt id, Practitioner name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none font-['Poppins'] text-[14px] text-[#64748B] placeholder-[#64748B] tracking-[0.28px] leading-[normal]"
            />
          </div>
        </div>

        {/* Timeline */}
        <div className="relative pl-4 sm:pl-6">
          {/* Vertical line */}
          <div className="absolute left-[7px] sm:left-[7px] top-2 bottom-0 w-[2px] bg-[#4B61FF]"></div>

          {/* Timeline items */}
          <div className="space-y-8">
            {filteredSessionData.length > 0 ? (
              filteredSessionData.map((session, index) => {
              // Calculate chronological session number based on original sessionData position
              const originalIndex = sessionData.findIndex(s => s.uniqueId === session.uniqueId);
              const sessionNumber = sessionData.length - originalIndex;
              
              return (
                <div key={session.uniqueId} className="relative">
                  {/* Timeline dot */}
                  <div className="absolute -left-[22px] sm:-left-[26px] top-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-white bg-[#4B61FF] flex justify-center items-center z-10">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#4B61FF] rounded-full"></div>
                  </div>

                  <div className="space-y-2">
                    {/* Session Header - As per Figma */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8">
                      <span className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] font-poppins">
                        {session.appointmentId}
                      </span>
                      <span className="text-xs sm:text-[14px] font-semibold text-[#171717] font-poppins">
                        {formatApiDate(session)}
                      </span>
                    </div>

                  {/* Session Card - Exact Figma Structure */}
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-2 sm:p-3">
                    {/* Main Content Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      {/* Left Section - Basic Info */}
                      <div className="flex-1 flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-0">
                        <div className="flex flex-col justify-between h-full">
                          <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                            {sessionNumber} - Consultation
                          </div>
                        <div className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                          {session.counsellorFirstName} {session.counsellorLastName}
                        </div>
                      </div>
                      <div className="flex flex-col sm:justify-between h-full sm:items-end w-full sm:w-[153px]">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 sm:w-3 sm:h-3 ${getStatusColor(session.status, session.currentStatus)} rounded-full`}></div>
                          <span className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] font-poppins">
                            {getStatusText(session.status, session.currentStatus)}
                          </span>
                        </div>
                        <div className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                          {session.mode || "Audio"}
                        </div>
                      </div>
                    </div>

                    {/* Vertical Separator */}
                    <div className="hidden sm:block w-px h-[57px] bg-[#E2E8F0]"></div>
                    {/* Horizontal Separator for mobile */}
                    <div className="block sm:hidden w-full h-px bg-[#E2E8F0] my-3"></div>

                    {/* Right Section - Risk and Actions */}
                    <div className="flex-1 sm:min-w-[250px] space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                          Overall Risk
                        </span>
                        <div
                          className="px-2 sm:px-3 py-0.5 h-5 sm:h-6 flex items-center justify-center rounded-full text-xs sm:text-[14px] font-medium uppercase tracking-[0.5px] font-poppins self-start sm:self-auto"
                          style={getRiskBadgeStyle(session.riskStatus)}
                        >
                          {getStatusText(session.riskStatus)}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:h-[21px] sm:items-start sm:justify-between gap-3 sm:gap-0">
                        <div className="flex-1 flex items-center gap-2">
                          <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-[#4B61FF]" />
                          <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                            Prescription
                          </span>
                          {session.url && (
                            <a 
                              href={session.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] underline font-poppins"
                            >
                              View
                            </a>
                          )}
                        </div>
                        <div className="flex-1 flex items-start sm:justify-end gap-2">
                          <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-[#4B61FF]" />
                          <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                            Notes
                          </span>
                          {session.notesUrl && (
                            <a 
                              href={session.notesUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] underline font-poppins"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Documents the client shared with this session (permanent log) */}
                  {(sharedDocs[session.appointmentId] || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                      <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                        Shared documents:
                      </span>
                      {sharedDocs[session.appointmentId].map((doc) =>
                        doc.viewable ? (
                          <button
                            key={doc.documentId}
                            className="flex items-center gap-1 bg-[#F8FAFC] border border-solid border-[#E2E8F0] rounded-md px-2 py-0.5 text-xs sm:text-[13px] font-medium text-[#4B61FF] font-poppins hover:bg-[#E8F4FF]"
                            onClick={() => setViewing({ appointmentId: session.appointmentId, doc })}
                            title="View document"
                          >
                            <FileText className="h-3 w-3" />
                            {doc.title}
                          </button>
                        ) : (
                          <span
                            key={doc.documentId}
                            className="flex items-center gap-1 bg-[#F7F7F7] rounded-md px-2 py-0.5 text-xs sm:text-[13px] text-[#8A8A8A] font-poppins"
                            title={REMOVED_LABELS[doc.status] ? `Document ${REMOVED_LABELS[doc.status]}` : "Access expired"}
                          >
                            <FileText className="h-3 w-3" />
                            {doc.title}
                            {REMOVED_LABELS[doc.status] && ` (${REMOVED_LABELS[doc.status]})`}
                          </span>
                        )
                      )}
                    </div>
                  )}

                  {/* Expanded Content - Only for expanded sessions */}
                  {session.expanded && (
                    <>
                      {/* Horizontal Separator */}
                      <div className="w-full h-px bg-[#E2E8F0] my-4"></div>
                      
                      {/* Session Findings and Recommendations */}
                      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                        {/* Session Findings */}
                        <div className="flex-1 space-y-4 lg:space-y-4">
                          <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                            Session Findings
                          </div>
                          <div className="space-y-4 lg:space-y-4">
                            {session.isFollowup && (
                              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                  Type
                                </span>
                                <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                  Followup
                                </span>
                              </div>
                            )}
                            {Object.entries(session.sessionFindings || {}).map(([key, value]) => (
                              <div key={key} className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                </span>
                                <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Vertical Separator */}
                        <div className="hidden lg:block w-px h-[156px] bg-[#E2E8F0]"></div>
                        {/* Horizontal Separator for mobile */}
                        <div className="block lg:hidden w-full h-px bg-[#E2E8F0] my-3"></div>

                        {/* Recommendations */}
                        <div className="flex-1 space-y-4 lg:space-y-4">
                          <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                            Recommendations
                          </div>
                          <div className="space-y-4 lg:space-y-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                Assessments
                              </span>
                              <div className="flex gap-3">
                                {session.recommendations?.assessments ? (
                                  <>
                                    <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                      {session.recommendations.assessments.name}
                                    </span>
                                    <a 
                                      href={session.recommendations.assessments.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] underline font-poppins"
                                      style={{ background: "transparent" }}
                                    >
                                      View
                                    </a>
                                  </>
                                ) : (
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    N/A
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                Exercises
                              </span>
                              <div className="flex gap-3">
                                {session.recommendations?.exercises ? (
                                  <>
                                    <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                      {session.recommendations.exercises.name}
                                    </span>
                                    <a 
                                      href={session.recommendations.exercises.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] underline font-poppins"
                                      style={{ background: "transparent" }}
                                    >
                                      View
                                    </a>
                                  </>
                                ) : (
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    N/A
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                Clinical Consultation
                              </span>
                              <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                {session.recommendations?.clinicalConsultation}
                              </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                Medical Consultation
                              </span>
                              <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                {session.recommendations?.medicalConsultation}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Horizontal Separator */}
                      <div className="w-full h-px bg-[#E2E8F0] my-4"></div>
                    </>
                  )}

                  {/* Final Horizontal Separator */}
                  <div className="w-full h-px bg-[#E2E8F0] my-4"></div>

                  {/* View More/Hide Button */}
                  <div className="flex justify-center">
                    <button 
                      onClick={() => toggleSession(session.uniqueId)}
                      className="text-xs sm:text-[14px] font-regular text-[#4B61FF] underline font-poppins tracking-[0.28px]"
                      style={{ background: "transparent" }}
                    >
                      {session.expanded ? "Hide" : "View more"}
                    </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })
            ) : (
              // No results message
              <div className="text-center py-8">
                <div className="text-[#64748B] text-[14px] font-poppins">
                  {searchQuery.trim() ? `No sessions found for appointment ID "${searchQuery.trim()}"` : 'No sessions available'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Session Modal */}
      <EditSessionModal
        visible={modalVisible}
        onClose={handleModalClose}
        sessionData={selectedSession}
        onSessionUpdated={onSessionUpdated}
      />
      <SharedDocumentViewer
        appointmentId={viewing?.appointmentId}
        doc={viewing?.doc || null}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}